import React from 'react';
import Input from 'components/atoms/Input';
import Button from 'components/atoms/Button';
import Link from 'components/atoms/Link';
import ErrorMessage from 'components/molecules/ErrorMessage';
import { useForm } from 'hooks/useForm';
import { validateForgotPasswordForm } from 'utils/validation';
import { ForgotPasswordFormProps } from 'types/organisms.types';
import { ForgotPasswordFormData } from 'types/auth.types';

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ 
  onSubmit, 
  onBackToLogin,
  loading = false,
  error = '',
  success = ''
}) => {
  const {
    values,
    errors,
    setValue,
    handleSubmit,
    isSubmitting
  } = useForm<ForgotPasswordFormData>(
    {
      email: ''
    },
    validateForgotPasswordForm
  );

  const [showError, setShowError] = React.useState<boolean>(true);
  const [showSuccess, setShowSuccess] = React.useState<boolean>(true);

  React.useEffect(() => {
    setShowError(!!error);
  }, [error]);

  React.useEffect(() => {
    setShowSuccess(!!success);
  }, [success]);

  const onFormSubmit = async (formValues: ForgotPasswordFormData): Promise<void> => {
    if (onSubmit) {
      await onSubmit(formValues);
    }
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    setShowError(true);
    handleSubmit(onFormSubmit);
  };

  const handleBackToLogin = (e: React.MouseEvent): void => {
    e.preventDefault();
    if (onBackToLogin) {
      onBackToLogin();
    }
  };

  const dismissError = (): void => {
    setShowError(false);
  };

  const dismissSuccess = (): void => {
    setShowSuccess(false);
  };

  const isFormDisabled = loading || isSubmitting;

  return (
    <div className="w-full max-w-md space-y-6">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Forgot Password?
        </h2>
        <p className="text-sm text-gray-600">
          Enter your email address and we'll send you a link to reset your password.
        </p>
      </div>

      {success && showSuccess && (
        <ErrorMessage 
          error={success}
          variant="info"
          onDismiss={dismissSuccess}
          className="mb-4"
        />
      )}

      {error && showError && (
        <ErrorMessage 
          error={error}
          onDismiss={dismissError}
          className="mb-4"
        />
      )}

      <form onSubmit={handleFormSubmit} className="space-y-4">
        <Input
          type="email"
          placeholder="Email Address"
          value={values.email}
          onChange={(e) => setValue('email', e.target.value)}
          error={errors.email}
          disabled={isFormDisabled}
          required
          autoComplete="email"
        />

        <Button
          type="submit"
          variant="teal"
          className="w-full py-3 font-semibold"
          loading={isFormDisabled}
          disabled={isFormDisabled}
        >
          {isFormDisabled ? 'Sending...' : 'Send Reset Link'}
        </Button>
      </form>

      <div className="text-center">
        <Link 
          onClick={handleBackToLogin} 
          className="text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors duration-200"
          disabled={isFormDisabled}
        >
          ← Back to Sign In
        </Link>
      </div>
    </div>
  );
};

export default ForgotPasswordForm;
